import { z } from "zod";

/** Connection apps whose drives can be synced into Vault. */
export const VAULT_SYNC_PROVIDER_IDS = [
  "microsoft_onedrive",
  "sharepoint",
  "box",
] as const;

/** Schema for Vault sync provider identifiers. */
export const VaultSyncProviderIdSchema = z.enum(VAULT_SYNC_PROVIDER_IDS);

/** A connection app whose drives can be synced into Vault. */
export type VaultSyncProviderId = z.infer<typeof VaultSyncProviderIdSchema>;

/** Values for whether a Vault sync is running or paused. */
export const VAULT_SYNC_STATUS_VALUES = ["ENABLED", "DISABLED"] as const;

/** Schema for whether a Vault sync is running or paused. */
export const VaultSyncStatusSchema = z.enum(VAULT_SYNC_STATUS_VALUES);

/** Whether a Vault sync is running or paused. */
export type VaultSyncStatus = z.infer<typeof VaultSyncStatusSchema>;

/** Kinds of upstream drive item a Vault external mapping can point at. */
export const VAULT_SYNC_ENTITY_TYPE_VALUES = ["file", "folder"] as const;

/** Schema for the kind of upstream drive item a Vault external mapping points at. */
export const VaultSyncEntityTypeSchema = z.enum(VAULT_SYNC_ENTITY_TYPE_VALUES);

/** The kind of upstream drive item a Vault external mapping points at. */
export type VaultSyncEntityType = z.infer<typeof VaultSyncEntityTypeSchema>;

/** The upstream drive item a Vault sync mirrors. `itemId` is "root" for a whole drive. */
export const VaultSyncIntegrationConfigSchema = z.object({
  driveId: z.string(),
  itemId: z.string(),
  name: z.string(),
  webUrl: z.string(),
});

/** The upstream drive item a Vault sync mirrors. */
export type VaultSyncIntegrationConfig = z.infer<
  typeof VaultSyncIntegrationConfigSchema
>;

/** Change cursor persisted per Vault sync; `token` is opaque to everything but the provider's sync client. */
export const VaultSyncCursorSchema = z.object({
  token: z.string(),
});

/** Change cursor persisted per Vault sync. */
export type VaultSyncCursor = z.infer<typeof VaultSyncCursorSchema>;

/** Upstream details recorded on a Vault external mapping. */
export const VaultExternalMetadataSchema = z.object({
  name: z.string(),
  parentExternalId: z.string().nullable(),
  size: z.number().nullable(),
  lastModifiedAt: z.string().nullable(),
});

/** Upstream details recorded on a Vault external mapping. */
export type VaultExternalMetadata = z.infer<typeof VaultExternalMetadataSchema>;

/** Latest run state of a Vault sync; `inProgress` is derived from a non-stale claim. */
export const VaultSyncProgressSchema = z.object({
  lastSuccessAt: z.string().nullable(),
  lastError: z.string().nullable(),
  inProgress: z.boolean(),
});

/** Latest run state of a Vault sync. */
export type VaultSyncProgress = z.infer<typeof VaultSyncProgressSchema>;

/** A Vault folder linked to an upstream cloud drive folder. */
export const VaultSyncSchema = z.object({
  id: z.string(),
  vaultSyncProviderId: VaultSyncProviderIdSchema,
  connectionId: z.string(),
  organizationFileId: z.string(),
  status: VaultSyncStatusSchema,
  integrationConfig: VaultSyncIntegrationConfigSchema,
  progress: VaultSyncProgressSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/** A Vault folder linked to an upstream cloud drive folder. */
export type VaultSync = z.infer<typeof VaultSyncSchema>;

/** Response for GET /organization-files/syncs. */
export const VaultSyncsResponseSchema = z.object({
  syncs: z.array(VaultSyncSchema),
});

/** Response for GET /organization-files/syncs. */
export type VaultSyncsResponse = z.infer<typeof VaultSyncsResponseSchema>;

/** Response for POST and PUT /organization-files/syncs. */
export const VaultSyncResponseSchema = z.object({
  sync: VaultSyncSchema,
});

/** Response for POST and PUT /organization-files/syncs. */
export type VaultSyncResponse = z.infer<typeof VaultSyncResponseSchema>;

/**
 * Body for POST /organization-files/syncs. `itemId` "root" syncs a whole drive;
 * the Vault folder is named after the upstream folder, drive owner, or library.
 */
export const CreateVaultSyncRequestSchema = z.object({
  connectionId: z.string(),
  driveId: z.string(),
  itemId: z.string(),
  parentFolderId: z.string().nullable(),
});

/** Body for POST /organization-files/syncs. */
export type CreateVaultSyncRequest = z.infer<
  typeof CreateVaultSyncRequestSchema
>;

/** Body for PUT /organization-files/syncs/:id, used to pause or resume a sync. */
export const UpdateVaultSyncRequestSchema = z.object({
  status: VaultSyncStatusSchema,
});

/** Body for PUT /organization-files/syncs/:id. */
export type UpdateVaultSyncRequest = z.infer<
  typeof UpdateVaultSyncRequestSchema
>;
